import { z } from 'zod'
import { SkuAssignmentMode } from './boxing-station.schemas'

const IsoDateTime = z.string().datetime({ offset: true })

/** Null means there isn't enough data yet. Never send 0 in its place. */
const MetricValue = z.number().nonnegative().nullable()

/** Worked out by the backend. The board only displays it and holds no thresholds. */
export const BOXING_STATION_METRICS_STATE_OPTIONS = [
  'below_target',
  'watch',
  'on_target',
  'not_enough_data',
  'offline',
] as const
export const BoxingStationMetricsState = z.enum(BOXING_STATION_METRICS_STATE_OPTIONS)
export type BoxingStationMetricsState = z.infer<typeof BoxingStationMetricsState>

export const IDLE_TIME_UNIT_OPTIONS = ['percent', 'minutes'] as const
export const IdleTimeUnit = z.enum(IDLE_TIME_UNIT_OPTIONS)
export type IdleTimeUnit = z.infer<typeof IdleTimeUnit>

export const BoxingStationMetrics = z.object({
  boxingStationId: z.number().int(),
  stationName: z.string(),
  state: BoxingStationMetricsState,
  /** The packer logged on at the station, or null when nobody is. */
  packer: z
    .object({
      pnpWorkerId: z.string().uuid(),
      name: z.string(),
    })
    .nullable(),
  /** SKUs assigned to the station, primary first. Empty when none is assigned. */
  skus: z.array(
    z.object({
      sku: z.number().int(),
      mode: SkuAssignmentMode,
    }),
  ),
  /** 100 is exactly on target, so 104 means 104%. */
  efficiencyPercent: MetricValue,
  boxesPerHour: z.object({
    /** Boxes packed over the last hour. */
    value: MetricValue,
    target: z.number().positive(),
  }),
  idleTime: z.object({
    value: MetricValue,
    limit: z.number().positive(),
    /** `percent`: share of time spent idle. `minutes`: time since the last packed box. */
    unit: IdleTimeUnit,
  }),
  /** The most recent team leader check-in, or null when there hasn't been one. */
  lastCheckIn: z
    .object({
      at: IsoDateTime,
      teamLeaderName: z.string(),
    })
    .nullable(),
})
export type BoxingStationMetrics = z.infer<typeof BoxingStationMetrics>

export const GetBoxingStationMetricsQuery = z.object({
  facilityId: z.coerce.number().int().positive(),
})
export type GetBoxingStationMetricsQuery = z.infer<typeof GetBoxingStationMetricsQuery>

export const GetBoxingStationMetricsResponse = z.object({
  /** When the server built this response. */
  generatedAt: IsoDateTime,
  stations: z.array(BoxingStationMetrics),
})
export type GetBoxingStationMetricsResponse = z.infer<typeof GetBoxingStationMetricsResponse>
